/**
 * Accounts (CRM) Board Page
 *
 * Kanban of customer accounts across pipeline stages. Backed by the unified
 * `cards` storage and the generic /api/cards surface — no account-specific
 * API. Account-specific fields (companyName, domain, ARR, ownerId) live in
 * `card.details` and are rendered through render-prop hooks.
 *
 * @module features/accounts/pages/AccountsBoardPage
 */

import React from 'react';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import {
  Flex,
  DrawerTrigger,
  Button as HiddenButton,
  TextField,
  TagBadge,
  cn,
  Text,
} from '@chatsdet/ui';
import { Buildings, Terminal, PuzzlePiece, Browser } from '@phosphor-icons/react';
import { BOARDS } from '@chatsdet/cards-plugins';
import { Board } from '../../../ui/components/board';
import type { BoardColumnConfig, BoardDropEvent } from '../../../ui/components/board';
import { BoardToolbar, boardSurfaceClassName } from '../../boards/BoardToolbar';
import { useCards, useMoveCard, type Card } from '../../cards/hooks/useCards';
import { CreateCardDialog } from '../../cards/components/CreateCardDialog';
import { CardDrawer } from '../../cards/components/CardDrawer';
import { AccountLabelsPanel, AccountLabelStrip } from '../components/AccountLabels';
import { LinkOrgPicker } from '../components/LinkOrgPicker';
import { CardFeatureFlagsPanel } from '../components/CardFeatureFlagsPanel';
import { useAccountUsage, type AccountUsageStats } from '../hooks/useAccountUsage';

const MANIFEST = BOARDS.account;

type AccountStage = string;

function AccountDetailFields({
  details,
  setDetails,
  commit,
}: {
  details: Record<string, unknown>;
  setDetails: (next: Record<string, unknown>) => void;
  /** When provided, persists the latest details (called on field blur). */
  commit?: () => void;
}) {
  const update = (key: string, value: string | number | null) => {
    const next = { ...details };
    if (value === null || value === '') delete next[key];
    else next[key] = value;
    setDetails(next);
  };

  return (
    <Flex direction="column" gap="sm">
      <TextField
        label="Company name"
        value={(details.company_name as string | undefined) ?? ''}
        onChange={(v) => update('company_name', v || null)}
        onBlur={() => commit?.()}
      />
      <TextField
        label="Domain"
        value={(details.domain as string | undefined) ?? ''}
        onChange={(v) => update('domain', v || null)}
        placeholder="example.com"
        onBlur={() => commit?.()}
      />
      <TextField
        label="ARR ($)"
        value={details.arr === undefined || details.arr === null ? '' : String(details.arr)}
        onChange={(v) => {
          if (v === '') return update('arr', null);
          const n = Number(v.replace(/[^0-9.-]/g, ''));
          update('arr', Number.isFinite(n) ? n : null);
        }}
        onBlur={() => commit?.()}
      />
    </Flex>
  );
}

/** One surface-usage chip — lit (colored) when used in the last 30 days, dimmed otherwise. */
function SurfaceChip({
  label,
  icon,
  used,
}: {
  label: string;
  icon: React.ReactNode;
  used: boolean;
}) {
  return (
    <TagBadge
      name={label}
      color={used ? 'green' : 'gray'}
      icon={icon}
      hideDot
      size="sm"
      className={used ? undefined : 'opacity-50'}
      tooltip={`${label}: ${used ? 'active' : 'inactive'} in the last 30 days`}
    />
  );
}

/** A labelled mini-metric for the card stat strip. */
function StatCell({
  label,
  value,
  title,
}: {
  label: string;
  value: React.ReactNode;
  title?: string;
}) {
  return (
    <div className="flex min-w-0 flex-col leading-none" title={title}>
      <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground/70">
        {label}
      </span>
      <span className="mt-1 text-xs font-semibold tabular-nums text-foreground/90">{value}</span>
    </div>
  );
}

/** Compact engagement stats for a card linked to a customer org. */
function AccountUsageRow({ stats }: { stats: AccountUsageStats }) {
  return (
    <div className="mt-2 flex flex-col gap-2 border-t border-border/60 pt-2">
      <div className="flex items-stretch gap-3">
        <StatCell
          label="DAU·WAU·MAU"
          value={`${stats.dau}·${stats.wau}·${stats.mau}`}
          title="Distinct active users — daily / weekly / monthly"
        />
        <div aria-hidden className="w-px self-stretch bg-border/60" />
        <StatCell
          label="Runs/day"
          value={stats.runsPerDay}
          title="Average test runs per day (30-day window)"
        />
        <div aria-hidden className="w-px self-stretch bg-border/60" />
        <StatCell
          label="Pass"
          value={stats.passRate === null ? '—' : `${stats.passRate}%`}
          title="Passing-run rate (30-day window)"
        />
      </div>
      <div className="flex flex-wrap gap-1">
        <SurfaceChip label="CLI" icon={<Terminal size={10} />} used={stats.usedCli} />
        <SurfaceChip label="Ext" icon={<PuzzlePiece size={10} />} used={stats.usedExtension} />
        <SurfaceChip label="UI" icon={<Browser size={10} />} used={stats.usedUi} />
      </div>
    </div>
  );
}

function AccountCard({
  item,
  isOverlay,
  stats,
}: {
  item: Card;
  isOverlay?: boolean;
  stats?: AccountUsageStats;
}) {
  const company = (item.details.company_name as string | undefined) ?? null;
  const domain = (item.details.domain as string | undefined) ?? null;
  const arr = item.details.arr as number | undefined;

  return (
    <div
      className={cn(
        'px-3 py-2.5 border border-border bg-card shadow-sm',
        'transition-all duration-150',
        !isOverlay && 'hover:shadow-md',
        isOverlay && 'border-primary/30'
      )}
    >
      <AccountLabelStrip tags={item.tags ?? []} className="mb-2" />
      <Text size="sm" weight="medium" className="leading-tight line-clamp-2">
        {item.title}
      </Text>
      {(company || domain) && (
        <Text size="xs" className="text-muted-foreground/80 line-clamp-1 mt-1">
          {[company, domain].filter(Boolean).join(' · ')}
        </Text>
      )}
      {typeof arr === 'number' && (
        <Text size="xs" weight="medium" className="mt-1">
          ${arr.toLocaleString()} ARR
        </Text>
      )}
      {item.linkedOrg && (
        <div className="mt-1.5">
          <TagBadge
            name={item.linkedOrg.name}
            color="blue"
            hideDot
            icon={<Buildings size={10} />}
          />
        </div>
      )}
      {item.linkedOrg && stats && <AccountUsageRow stats={stats} />}
    </div>
  );
}

export function AccountsBoardPage() {
  const navigate = useNavigate();
  const search = useRouterState({
    select: (s) => s.location.search as { cardId?: string; search?: string },
  });
  const activeCardId = search.cardId ?? null;
  const searchText = search.search ?? '';
  const supportsSearch = MANIFEST.features.includes('search');

  const { data: cards = [], isLoading } = useCards('account', { search: searchText });
  const { data: usageByOrg } = useAccountUsage();
  const move = useMoveCard('account');

  const itemsByColumn = React.useMemo(() => {
    const map: Record<AccountStage, Card[]> = {};
    for (const stage of MANIFEST.stages) map[stage.value] = [];
    for (const c of cards) {
      if (!map[c.stage]) map[c.stage] = [];
      map[c.stage].push(c);
    }
    return map;
  }, [cards]);

  const columns: BoardColumnConfig<AccountStage>[] = MANIFEST.stages.map((stage) => ({
    id: stage.value,
    label: stage.label,
    isLoading,
    totalItems: itemsByColumn[stage.value]?.length ?? 0,
    emptyLabel: 'No accounts',
  }));

  const handleDrop = (event: BoardDropEvent<AccountStage, Card>) => {
    move.mutate({
      id: event.item.id,
      stage: event.toColumnId,
      beforeItemId: event.beforeItemId,
      afterItemId: event.afterItemId,
    });
  };

  const handleCardClick = (item: Card) => {
    void navigate({ to: '/boards/accounts', search: { ...search, cardId: item.id } });
  };

  const handleDrawerOpenChange = (open: boolean) => {
    if (!open) void navigate({ to: '/boards/accounts', search: { ...search, cardId: undefined } });
  };

  const handleSearchChange = (value: string) => {
    void navigate({
      to: '/boards/accounts',
      search: {
        ...search,
        search: value.trim().length > 0 ? value : undefined,
      },
    });
  };

  return (
    <>
      <div className="flex min-h-0 flex-1 flex-col">
        <BoardToolbar
          searchValue={searchText}
          onSearchChange={supportsSearch ? handleSearchChange : undefined}
          searchPlaceholder="Search accounts"
          actions={
            <CreateCardDialog
              boardType="account"
              renderDetailFields={(details, setDetails) => (
                <AccountDetailFields details={details} setDetails={setDetails} />
              )}
            />
          }
        />

        <Board<AccountStage, Card>
          className={boardSurfaceClassName}
          columns={columns}
          itemsByColumn={itemsByColumn}
          getItemId={(c) => c.id}
          getItemColumnId={(c) => c.stage}
          onDrop={handleDrop}
          onCardClick={handleCardClick}
          renderCard={(item, { isOverlay }) => (
            <AccountCard
              item={item}
              isOverlay={isOverlay}
              stats={item.linkedOrg ? usageByOrg?.[item.linkedOrg.id] : undefined}
            />
          )}
        />
      </div>

      <DrawerTrigger isOpen={!!activeCardId} onOpenChange={handleDrawerOpenChange}>
        <HiddenButton className="hidden">Open</HiddenButton>
        {activeCardId ? (
          <CardDrawer
            cardId={activeCardId}
            boardType="account"
            onClose={() => handleDrawerOpenChange(false)}
            renderDetailFields={(details, setDetails, commit) => (
              <AccountDetailFields details={details} setDetails={setDetails} commit={commit} />
            )}
            renderExtraSections={(card) => (
              <>
                <LinkOrgPicker card={card} />
                <CardFeatureFlagsPanel card={card} />
                <AccountLabelsPanel card={card} />
              </>
            )}
          />
        ) : (
          <div />
        )}
      </DrawerTrigger>
    </>
  );
}
